import { useState, useRef, useEffect } from 'react';
import { motion } from 'motion/react';
import { ArrowLeft, Heart, MessageCircle, Share2, Megaphone, MoreHorizontal, MapPin, Lock, Pin, Ban } from 'lucide-react';
import { Button } from './ui/button';
import { formatTimeAgo } from './utils/timeFormat';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { ShareYapModal } from './ShareYapModal';
import { ShoutYapModal } from './ShoutYapModal';
import { ShoutOptionsModal } from './ShoutOptionsModal';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { YapService } from '../services/api';
import { VoiceReplyBubble } from './VoiceReplyBubble';
import { User } from '../types';
import { FlagImage } from './utils/countryFlags';
import { PostMenu } from './ui/PostMenu';
import { getRankRingParams, getRankBadge } from './utils/rankUtils';
import { useYapInteractions } from './YapInteractionsContext';
import { useUserData } from './UserDataContext';
import { useContentFilter } from '../hooks/useContentFilter';
import { EmbeddedYap } from './EmbeddedYap';
import { ParallaxBackground } from './utils/parallax';
import { ImageWithPreview } from './ImageWithPreview';
import { getYapAuthorInfo, getYapInteractionId, getOriginalYapId, isShoutedYap, getYapPostType } from './utils/yapUtils';
import { EVENTS, SCREENS } from '../constants/navigation';
import { SafeStorage } from '../utils/safeStorage';
import { PressableCard } from './ui/PressableCard';
import { LikeButton } from './ui/LikeButton';

/** Brand blue for the active tab label and its underline. Same value in both themes. */
const TAB_ACTIVE_COLOR = '#1D9BF0';

interface FriendYapsScreenProps {
  friend: User;
  onBack: () => void;
}

export function FriendYapsScreen({ friend, onBack }: FriendYapsScreenProps) {
  const { toggleLike, isYapLiked, getLikeCount, getShoutCount, isPostLiked, getPostLikeCount, toggleShout: toggleShoutGlobal, isYapShouted } = useYapInteractions();
  const { blockUser, userProfile, addShout, isTribeMember, isForumMember } = useUserData();
  const { isBlockedContent } = useContentFilter();
  const [activeTab, setActiveTab] = useState<'pinned' | 'all'>('pinned');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showShoutModal, setShowShoutModal] = useState(false);
  const [showShoutOptionsModal, setShowShoutOptionsModal] = useState(false);
  const [selectedYap, setSelectedYap] = useState<any>(null);
  const [selectedYapToShare, setSelectedYapToShare] = useState<any>(null);
  const viewportRef = useRef<HTMLDivElement>(null);



  // ── The author's yaps, from Firestore ────────────────────────────────────
  //
  // This screen used to read from ./utils/mockData — getUserById,
  // getPinnedYapsForUser and getUserYapsWithShouts are filters over hardcoded
  // arrays of sample users and sample yaps. A real Firebase uid matches nothing
  // in them, so "[Name]'s Yaps" showed "No yaps yet" for EVERY real account, and
  // the Pinned tab was empty for the same reason. It looked like a data problem
  // with individual profiles; it was that the screen never queried anything.
  //
  // The giveaway was that the profile one tap earlier showed a Recent Yap just
  // fine: ProfileScreen calls YapService.fetchUserYaps, the real query, and this
  // screen did not.
  const [yaps, setYaps] = useState<any[]>([]);
  const [loadState, setLoadState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!friend?.id) return;
    let cancelled = false;
    setLoadState('loading');

    (async () => {
      try {
        const fetched = await YapService.fetchUserYaps(String(friend.id));
        if (cancelled) return;
        setYaps(fetched ?? []);
        setLoadState('ready');
      } catch (e) {
        if (cancelled) return;
        console.error('[FriendYapsScreen] Failed to load yaps:', e);
        // An error state, not an empty one. fetchUserYaps swallows its own
        // failures and returns [], so a permission error or a missing index
        // would otherwise be indistinguishable from a profile with nothing on
        // it — a failed load must never read as "this person has never posted".
        setLoadState('error');
      }
    })();

    return () => { cancelled = true; };
  }, [friend?.id, reloadKey]);

  // Pinned is derived from the same fetch rather than a second query: pinYap
  // writes isPinned onto the yap itself (and mirrors the id onto the user doc),
  // so the list is already here. Same approach UserYapsScreen takes.
  const pinnedYaps = yaps.filter((y: any) => y.isPinned === true);

  const displayYaps = (activeTab === 'pinned' ? pinnedYaps : yaps)
    .filter((y: any) => !isBlockedContent(y));

  // Handle scroll restoration
  useEffect(() => {
    const savedPos = SafeStorage.getItem(`scroll_pos_friendyaps_${friend.id}`);
    if (savedPos && viewportRef.current) {
      const targetPosition = parseInt(savedPos, 10);
      if (isNaN(targetPosition) || targetPosition <= 0) {
        SafeStorage.removeItem(`scroll_pos_friendyaps_${friend.id}`);
        return;
      }

      console.log(`[FriendYapsScreen] Starting robust scroll restoration for ${friend.id} to ${targetPosition}`);
      
      let attempts = 0;
      const maxAttempts = 10;
      
      const tryScroll = () => {
        if (!viewportRef.current) return;
        
        const currentContainer = viewportRef.current;
        const maxScroll = currentContainer.scrollHeight - currentContainer.clientHeight;
        
        currentContainer.scrollTo(0, targetPosition);
        
        if (Math.abs(currentContainer.scrollTop - targetPosition) < 2 || (maxScroll > 0 && currentContainer.scrollTop >= maxScroll && targetPosition >= maxScroll)) {
          console.log(`[FriendYapsScreen] Scroll restoration successful on attempt ${attempts}`);
          SafeStorage.removeItem(`scroll_pos_friendyaps_${friend.id}`);
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(tryScroll, 100);
        } else {
          console.warn(`[FriendYapsScreen] Scroll restoration gave up. Target: ${targetPosition}, Current: ${currentContainer.scrollTop}`);
          SafeStorage.removeItem(`scroll_pos_friendyaps_${friend.id}`);
        }
      };

      const timer = setTimeout(tryScroll, 50);
      return () => clearTimeout(timer);
    }
  }, [friend.id]);
  const handleYapClick = (yap: any) => {
    // Save scroll position
    if (viewportRef.current) {
      SafeStorage.setItem(`scroll_pos_friendyaps_${friend.id}`, viewportRef.current.scrollTop.toString());
    }
    // Navigate to yap detail for regular yaps
    window.dispatchEvent(new CustomEvent('navigateToYapDetail', {
      detail: { ...yap, author: friend.name, avatar: friend.image, username: friend.username }
    }));
  };

  const handleShoutClick = (shout: any) => {
    // Save scroll position
    if (viewportRef.current) {
      SafeStorage.setItem(`scroll_pos_friendyaps_${friend.id}`, viewportRef.current.scrollTop.toString());
    }
    // Navigate to shout detail screen for shouted yaps
    console.log('[FriendYapsScreen] Navigating to Shout Detail:', shout.id);
    window.dispatchEvent(new CustomEvent('navigateToShoutDetail', {
      detail: shout
    }));
  };

  const handleShareYap = (yap: any) => {
    setSelectedYapToShare(yap);
    setShowShareModal(true);
  };

  const handleShoutWithCaption = (caption: string, images: string[], friendsOnly: boolean, audioData?: { url: string, duration: number }) => {
    if (selectedYap) {
      // friendsOnly forwarded — the toast below already promises it.
      toggleShoutGlobal(selectedYap.id, caption, images, audioData, friendsOnly);
      toast.success(friendsOnly ? 'Yap shouted to friends only!' : 'Yap shouted with your caption!', { duration: 1500 });
      setShowShoutModal(false);
      setSelectedYap(null);
    }
  };

  const handleShoutWithoutCaption = () => {
    if (selectedYap) {
      toggleShoutGlobal(selectedYap.id);
      toast.success('Yap shouted to your profile!', { duration: 1500 });
      setShowShoutModal(false);
      setSelectedYap(null);
    }
  };




  const handleToggleShout = (yap: any, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedYap(yap);
    setShowShoutModal(true);
  };

  const handleBlockUser = () => {
    try {
      blockUser(String(friend.id));
      toast.success(`${friend.name} has been blocked`);
      onBack(); // Go back after blocking
    } catch (error) {
      console.error('Failed to block user:', error);
      toast.error(`Failed to block ${friend.name}`);
    }
  };

  const firstName = friend.name.split(' ')[0];

  return (
    // No pb-20 on the root, unlike the screens that sit behind the bottom
    // nav: /profile/:userId/yaps is not one of BOTTOM_NAV_PATHS, so that padding reserved
    // 80px of the viewport for a bar that never renders and the scrolling
    // list below was clipped 80px short of the bottom.
    <div className="w-full h-screen bg-light-pattern dark:bg-[#0D0D0D] relative">
      <div className="absolute inset-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>
      <div className="w-full max-w-2xl mx-auto h-full flex flex-col relative z-10">
        {/* Header */}
        <div className="px-4 pb-4 border-b border-black/10 dark:border-white/10 bg-light-pattern dark:bg-[#121212]"
          style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))' }}>
          <div className="flex items-center gap-3 mb-4">
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={onBack}
              className="w-10 h-10 rounded-full bg-black/5 dark:bg-white/10 flex items-center justify-center"
            >
              <ArrowLeft className="w-5 h-5 text-black dark:text-white" />
            </motion.button>
            <div>
              <h1 className="text-2xl text-black dark:text-white">{friend.name}'s Yaps</h1>
              <p className="text-sm text-[#666666] dark:text-[#999999]">{friend.username}</p>
            </div>
          </div>

          {/* Tabs
              Active label and underline share one colour token, so they cannot
              drift apart again: the label was #F39C12 (orange) while the
              underline was an orange-to-red gradient in light mode and an
              indigo one in dark, so they never matched in either theme.

              Set inline rather than as text-[#1D9BF0] / from-[#1D9BF0]:
              src/index.css is committed pre-compiled Tailwind and contains no
              occurrence of this colour, so those arbitrary-value classes would
              compile to nothing and the label would silently inherit. */}
          <div className="flex gap-4 border-b border-black/10 dark:border-white/10">
            <button
              onClick={() => setActiveTab('pinned')}
              className={`pb-2 px-1 relative ${activeTab === 'pinned' ? '' : 'text-[#666666] dark:text-[#999999]'}`}
              style={activeTab === 'pinned' ? { color: TAB_ACTIVE_COLOR } : undefined}
            >
              Pinned
              {activeTab === 'pinned' && (
                <div
                  className="absolute bottom-0 left-0 right-0"
                  style={{ height: 2, backgroundColor: TAB_ACTIVE_COLOR }}
                />
              )}
            </button>
            <button
              onClick={() => setActiveTab('all')}
              className={`pb-2 px-1 relative ${activeTab === 'all' ? '' : 'text-[#666666] dark:text-[#999999]'}`}
              style={activeTab === 'all' ? { color: TAB_ACTIVE_COLOR } : undefined}
            >
              All Yaps
              {activeTab === 'all' && (
                <div
                  className="absolute bottom-0 left-0 right-0"
                  style={{ height: 2, backgroundColor: TAB_ACTIVE_COLOR }}
                />
              )}
            </button>
          </div>
        </div>

        {/* Yaps List */}
        <ScrollArea className="flex-1" style={{ minHeight: 0 }} viewportRef={viewportRef}>
          <div className="p-4 space-y-4">
            {displayYaps.map((item) => {
              // Cast to any to handle lack of strict type checks for 'type' on these interfaces
              const itemAny = item as any;
              const isShout = itemAny.type === 'shouted';
              // For shouts, we need to access the original yap data structure
              const yap = isShout ? itemAny.originalYap : itemAny;
              const hasShoutCaption = isShout && itemAny.shoutCaption;
              const authorInfo = getYapAuthorInfo(itemAny);
              const isGroupInteractionAllowed = 
                (!yap?.tribeId || isTribeMember(yap.tribeId)) && 
                (!yap?.forumId || isForumMember(yap.forumId));

              return hasShoutCaption ? (
                // Shouted yap WITH caption - display as regular yap with embedded original
                <PressableCard
                  key={item.id}
                  className="rounded-2xl bg-[#F5F5F5] dark:bg-[#1a1a1a] border border-black/5 dark:border-white/10 overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => handleShoutClick(itemAny)}
                >
                  {/* Shout Header */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-bold text-black dark:text-white truncate">
                            {authorInfo.username}
                          </span>
                          <FlagImage countryName={authorInfo.country} size="w80" className="w-4 aspect-3/2 object-contain rounded" />
                          <span className="text-xs text-[#666666] dark:text-[#a0a0a0]">•</span>
                          {!itemAny.isAnonymous && (
                            <>
                              <div className="flex items-center gap-1 shrink-0">
                                <MapPin style={{ width: '10px', height: '10px' }} className="text-[#4478ea] shrink-0" />
                                <span className="text-[10px] text-[#666666] dark:text-[#a0a0a0]">{authorInfo.location}</span>
                              </div>
                              <span className="text-xs text-[#666666] dark:text-[#a0a0a0]">•</span>
                            </>
                          )}
                          <span className="text-xs text-[#666666] dark:text-[#a0a0a0]">{formatTimeAgo(itemAny.timestamp || itemAny.time)}</span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-2 text-[10px] font-bold uppercase tracking-wider text-[#4478ea] dark:text-[#6090ff]">
                          <Megaphone className="w-3 h-3 fill-[#4478ea]" />
                          <span>Shouted</span>
                          {itemAny.friendsOnly && (
                            <span className="flex items-center gap-1 ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#E74C3C]/20 text-[#E74C3C]">
                              <Lock className="w-2.5 h-2.5" />
                              Friends
                            </span>
                          )}
                        </div>
                      </div>
                    {/* Shared menu — see ui/PostMenu. This screen previously
                        offered only Block, Report and Share; authors got no
                        Edit, Delete, Copy Link or Download at all. */}
                    <PostMenu
                      post={item as any}
                      surface="feed"
                      collectionName={(item as any)?.isShout ? 'shouts' : 'yaps'}
                      onChanged={() => window.dispatchEvent(new CustomEvent('yapUpdated'))}
                    />
                    </div>

                    {/* Shout Caption */}
                    {itemAny.shoutCaption && (
                      <p className="text-[#666666] dark:text-[#E5E5E5] mb-3">{itemAny.shoutCaption}</p>
                    )}

                    {itemAny.audioUrl && (
                      <div className="mb-3">
                        <VoiceReplyBubble 
                          audioUrl={itemAny.audioUrl} 
                          duration={itemAny.duration}
                          className="w-full"
                        />
                      </div>
                    )}

                    {/* Shout Images */}
                    {itemAny.shoutImages && itemAny.shoutImages.length > 0 && (
                      <div className={`mb-3 gap-2 ${itemAny.shoutImages.length === 1
                        ? 'grid grid-cols-1'
                        : 'grid grid-cols-2'
                        }`}>
                        {itemAny.shoutImages.map((img: string, idx: number) => (
                          <div key={idx} className="rounded-xl overflow-hidden border border-black/5 dark:border-white/10 bg-black/5 dark:bg-white/5">
                            <ImageWithPreview
                              src={img}
                              alt={`Shout image ${idx + 1}`}
                              className="w-full h-auto object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Embedded Original Yap - Clickable to go to original yap detail */}
                      <EmbeddedYap
                        yapId={getOriginalYapId(yap)}
                        username={yap?.username || yap?.userUsername || `@${(yap?.userName || 'unknown').replace(/@/g, '').toLowerCase()}`}
                        authorName={yap?.userName || yap?.author || friend.name}
                        avatar={yap?.userAvatar || yap?.avatar || friend.image}
                        timestamp={yap?.timestamp || yap?.time || ''}
                        content={yap?.content || ''}
                        poll={(yap as any)?.poll}
                        image={yap?.image}
                        country={yap?.country || friend.country}
                        showLocation={yap?.showLocation ?? true}
                        likes={yap?.likes || 0}
                        comments={yap?.replies || yap?.comments || 0}
                        shouts={yap?.shouts || 0}
                        audioUrl={yap?.audioUrl}
                        duration={yap?.duration}
                        isDeleted={yap?.deleted}
                        onYapClick={() => handleYapClick(yap)}
                        maxLines={3}
                        minimal={true}
                        showActions={false}
                        isInteractionAllowed={isGroupInteractionAllowed}
                      />

                    {/* Shout Actions - Only show shouter's reactions */}
                    <div className="flex items-center gap-6 mt-3">
                      <LikeButton
                        postId={itemAny.id}
                        postType="shout"
                        originalLikeCount={itemAny.likes || 0}
                        canInteract={() => isGroupInteractionAllowed}
                        onBlockedInteraction={() => {
                          toast.error("Members only", { description: `Join this ${yap.tribeId ? 'tribe' : 'forum'} to like yaps.` });
                        }}
                      />

                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        className="flex items-center gap-2 text-[#666666] dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-[#4478ea] transition-colors touch-clickable"
                      >
                        <MessageCircle className="w-5 h-5" />
                        <span className="text-sm">{itemAny.comments || 0}</span>
                      </motion.button>

                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShareYap(itemAny);
                        }}
                        className="flex items-center gap-2 text-[#666666] dark:text-[#E5E5E5] hover:text-[#F39C12] dark:hover:text-[#4478ea] transition-colors touch-clickable"
                      >
                        <Share2 className="w-5 h-5" />
                      </motion.button>
                    </div>
                  </div>
                </PressableCard>
              ) : isShout ? (
                // Shouted yap WITHOUT caption - show with embedded original
                <PressableCard
                  key={itemAny.id}
                  onClick={() => handleShoutClick(itemAny)}
                  className="rounded-2xl bg-[#F5F5F5] dark:bg-[#1a1a1a] border border-black/5 dark:border-white/10 overflow-hidden cursor-pointer hover:bg-[#E5E5E5] dark:hover:bg-[#2a2a2a] transition-colors"
                >
                  {/* Shouter's Profile with uniform Shouted badge */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-sm font-bold text-black dark:text-white truncate">
                            {authorInfo.username}
                          </span>
                          <FlagImage countryName={authorInfo.country} size="w80" className="w-4 aspect-3/2 object-contain rounded" />
                          <span className="text-xs text-[#666666] dark:text-[#a0a0a0]">•</span>
                          {!itemAny.isAnonymous && (
                            <>
                              <div className="flex items-center gap-1 shrink-0">
                                <MapPin style={{ width: '10px', height: '10px' }} className="text-[#4478ea] shrink-0" />
                                <span className="text-[10px] text-[#666666] dark:text-[#a0a0a0]">{authorInfo.location}</span>
                              </div>
                              <span className="text-xs text-[#666666] dark:text-[#a0a0a0]">•</span>
                            </>
                          )}
                          <span className="text-xs text-[#666666] dark:text-[#a0a0a0]">{formatTimeAgo(itemAny.timestamp || itemAny.time)}</span>
                        </div>
                        <div className="flex items-center gap-1.5 mt-2 text-[10px] font-bold uppercase tracking-wider text-[#4478ea] dark:text-[#6090ff]">
                          <Megaphone className="w-3 h-3 fill-[#4478ea]" />
                          <span>Shouted</span>
                          {itemAny.friendsOnly && (
                            <span className="flex items-center gap-1 ml-2 text-[10px] px-2 py-0.5 rounded-full bg-[#E74C3C]/20 text-[#E74C3C]">
                              <Lock className="w-2.5 h-2.5" />
                              Friends
                            </span>
                          )}
                        </div>
                      </div>
                    {/* Shared menu — see ui/PostMenu. This screen previously
                        offered only Block, Report and Share; authors got no
                        Edit, Delete, Copy Link or Download at all. */}
                    <PostMenu
                      post={item as any}
                      surface="feed"
                      collectionName={(item as any)?.isShout ? 'shouts' : 'yaps'}
                      onChanged={() => window.dispatchEvent(new CustomEvent('yapUpdated'))}
                    />
                    </div>

                    {/* Embedded Original Yap - Minimal mode (username only) */}
                      <EmbeddedYap
                        yapId={getYapInteractionId(yap)}
                        username={yap?.username || yap?.userUsername || `@${yap?.userName?.toLowerCase()}`}
                        authorName={yap?.userName || yap?.author || friend.name}
                        avatar={yap?.userAvatar || yap?.avatar || friend.image}
                        timestamp={yap?.timestamp || yap?.time || ''}
                        content={yap?.content || ''}
                        poll={(yap as any)?.poll}
                        image={yap?.image}
                        country={yap?.country || friend.country}
                        showLocation={yap?.showLocation ?? true}
                        likes={yap?.likes || 0}
                        comments={yap?.replies || yap?.comments || 0}
                        shouts={yap?.shouts || 0}
                        isDeleted={yap?.deleted}
                        onYapClick={(e) => {
                          e?.stopPropagation();
                          // Navigate to original yap detail
                          handleYapClick(yap);
                        }}
                        maxLines={2}
                        minimal={true}
                        className="mb-3"
                        showActions={false}
                        isInteractionAllowed={isGroupInteractionAllowed}
                      />

                    {/* Shout Stats */}
                    <div className="flex items-center gap-4 text-sm text-[#999999] dark:text-[#808080]">
                      <span>❤️¸ {itemAny.likes}</span>
                      <span>💬 {itemAny.comments}</span>
                    </div>
                  </div>
                </PressableCard>
              ) : (
                // Regular yap
                <PressableCard
                  key={itemAny.id}
                  className="p-4 rounded-2xl bg-[#F5F5F5] dark:bg-[#1a1a1a] border border-black/5 dark:border-white/10"
                >
                  {itemAny.isPinned && (
                    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-[#F39C12]/20 dark:border-[#4478ea]/20">
                      <Pin className="w-4 h-4 text-[#F39C12] dark:text-[#4478ea]" />
                      <span className="text-sm text-[#F39C12] dark:text-[#4478ea]">Pinned Yap</span>
                    </div>
                  )}

                  <div className="flex items-start justify-between mb-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm font-bold text-black dark:text-white truncate">
                          {authorInfo.username}
                        </span>
                        <FlagImage countryName={authorInfo.country} size="w80" className="w-4 aspect-3/2 object-contain rounded" />
                        <span className="text-xs text-[#666666] dark:text-[#a0a0a0]">•</span>
                        {/* formatTimeAgo, like the two other timestamps on this screen. Raw
                            `time` only ever looked right because the mock data it
                            used to read held a pre-formatted "2h ago"; the real
                            query returns an ISO string, which printed verbatim. */}
                        <span className="text-xs text-[#666666] dark:text-[#a0a0a0]">{formatTimeAgo(itemAny.timestamp || itemAny.time)}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1.5">
                        {itemAny.isPinned && (
                          <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#F39C12] text-white font-bold uppercase tracking-wider">
                            <Pin className="w-2.5 h-2.5" />
                            Pinned
                          </span>
                        )}
                        {itemAny.visibility === 'friends' && (
                          <span className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#E74C3C]/20 text-[#E74C3C] font-bold uppercase tracking-wider">
                            <Lock className="w-2.5 h-2.5" />
                            Friends
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Shared menu — see ui/PostMenu. This screen previously
                        offered only Block, Report and Share; authors got no
                        Edit, Delete, Copy Link or Download at all. */}
                    <PostMenu
                      post={item as any}
                      surface="feed"
                      collectionName={(item as any)?.isShout ? 'shouts' : 'yaps'}
                      onChanged={() => window.dispatchEvent(new CustomEvent('yapUpdated'))}
                    />
                  </div>

                  <div onClick={() => handleYapClick(itemAny)} className="cursor-pointer">
                    <p className="text-[#666666] dark:text-[#E5E5E5] mb-4">{itemAny.content}</p>

                    {itemAny.audioUrl && (
                      <div className="mb-4">
                        <VoiceReplyBubble 
                          audioUrl={itemAny.audioUrl} 
                          duration={itemAny.duration}
                          className="w-full"
                        />
                      </div>
                    )}

                    {/* Images if exists (Regular Yap) */}
                    {(itemAny.image || (itemAny.images && itemAny.images.length > 0)) && (
                      <div className={`mb-4 ${(itemAny.images && itemAny.images.length >= 2) || (itemAny.image && itemAny.images && itemAny.images.length >= 1) ? 'grid grid-cols-2' : 'grid grid-cols-1'}`}>
                        {itemAny.images && itemAny.images.length > 0 ? (
                          itemAny.images.slice(0, 2).map((img: string, idx: number) => (
                            <div key={idx} className="rounded-xl overflow-hidden border border-black/5 dark:border-white/10 bg-black/5 dark:bg-white/5">
                              <ImageWithPreview
                                src={img}
                                alt={`Yap content ${idx + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))
                        ) : (
                          <div className="w-full rounded-2xl overflow-hidden border border-black/5 dark:border-white/10 bg-black/5 dark:bg-white/5">
                            <ImageWithPreview
                              src={itemAny.image}
                              alt="Yap content"
                              className="w-full h-auto object-cover"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex items-center gap-6">
                      <LikeButton
                        postId={itemAny.id}
                        postType={getYapPostType(itemAny)}
                        originalLikeCount={itemAny.likes || 0}
                        canInteract={() => isGroupInteractionAllowed}
                        onBlockedInteraction={() => {
                          toast.error("Members only", { description: `Join this ${itemAny.tribeId ? 'tribe' : 'forum'} to like yaps.` });
                        }}
                      />

                      {/* No shout button on friend-only yaps */}
                      {itemAny.visibility !== 'friends' && (
                        <motion.button
                          whileTap={{ scale: 0.95 }}
                          onClick={(e) => {
                            if (!isGroupInteractionAllowed) {
                              toast.error("Members only", { description: `Join this ${itemAny.tribeId ? 'tribe' : 'forum'} to shout.` });
                              return;
                            }
                            handleToggleShout(itemAny, e);
                          }}
                          className={`flex items-center gap-2 transition-colors touch-clickable ${!isGroupInteractionAllowed ? 'opacity-50' : isYapShouted(itemAny.id) ? 'text-[#F39C12]' : 'text-[#666666] dark:text-[#E5E5E5] hover:text-[#F39C12]'}`}
                        >
                          <Megaphone className={`w-5 h-5 ${isYapShouted(itemAny.id) ? 'fill-[#F39C12]' : ''}`} />
                          <span className="text-sm">{getShoutCount(itemAny.shouts || 0, itemAny.id)}</span>
                        </motion.button>
                      )}

                      <div className="flex items-center gap-2 text-[#666666] dark:text-[#E5E5E5]" onClick={() => handleYapClick(itemAny)}>
                        <MessageCircle className="w-5 h-5" />
                        <span className="text-sm">{itemAny.comments}</span>
                      </div>

                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShareYap(itemAny);
                        }}
                        className="flex items-center gap-2 text-[#666666] dark:text-[#E5E5E5] hover:text-[#F39C12] transition-colors touch-clickable"
                      >
                        <Share2 className="w-5 h-5" />
                      </motion.button>
                    </div>
                  </div>
                </PressableCard>
              )
            })}

            {loadState === 'loading' && (
              <div className="flex justify-center py-12">
                <div className="w-6 h-6 rounded-full border-2 border-[#4478ea]/30 border-t-[#4478ea] animate-spin" />
              </div>
            )}

            {loadState === 'error' && (
              <div className="text-center py-12">
                <p className="text-[#666666] dark:text-[#999999] mb-3">Couldn't load these yaps.</p>
                <button
                  onClick={() => setReloadKey(k => k + 1)}
                  className="px-5 py-2 rounded-xl text-white font-bold"
                  style={{ backgroundColor: '#1D9BF0' }}
                >
                  Try again
                </button>
              </div>
            )}

            {/* Only once the query has actually resolved. Showing this while the
                fetch was still in flight is what made every profile look empty
                for a moment even after the data layer was correct. */}
            {loadState === 'ready' && displayYaps.length === 0 && (
              <div className="text-center py-12">
                <p className="text-[#666666] dark:text-[#999999]">
                  {activeTab === 'pinned' ? 'No pinned yaps yet' : 'No yaps yet'}
                </p>
              </div>
            )}
            <div className="h-32" /> {/* Bottom clearance for nav */}
          </div>
        </ScrollArea>
      </div>
             {/* Share Yap Modal */}
      {selectedYapToShare && (
        <ShareYapModal
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            setSelectedYapToShare(null);
          }}
          yap={selectedYapToShare}
        />
      )}

      {/* Shout Details Modal */}
      {selectedYap && (
        <ShoutYapModal
          isOpen={showShoutModal}
          onClose={() => {
            setShowShoutModal(false);
            setSelectedYap(null);
          }}
          yap={selectedYap}
          onShoutWithCaption={handleShoutWithCaption}
          onShoutWithoutCaption={handleShoutWithoutCaption}
        />
      )}
    </div>
  );
}
